import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Paper,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import CustomButton from "../UI/CustomButton";
import SaveForFollowingVisitsCheckbox, {
  canSaveForFollowingVisits,
} from "./SaveForFollowingVisitsCheckbox";
import AddNewModal from "../Modals/AddNewTrial";
import useGetExternalMediaTypes from "../../hooks/useGetExternalMediaTypes";
import useGetVisitExternalPhotoTypes from "../../hooks/useGetVisitExternalPhotoTypes";
import useGetVisitExternalVideoTypes from "../../hooks/useGetVisitExternalVideoTypes";
import useUpdateVisitExternalPhotoTypesMutation from "../../hooks/useUpdateVisitExternalPhotoTypesMutation";
import useUpdateVisitExternalVideoTypesMutation from "../../hooks/useUpdateVisitExternalVideoTypesMutation";
import useCreateExternalMediaTypeMutation from "../../hooks/useCreateExternalMediaType";
import { UPLOAD_TYPES } from "../../utils/constants";

const FALLBACK_HELPER_TEXT =
  "If none are selected, staff will see the full trial catalog when uploading.";

const selectVisitConfig = (data) => data;

const ExternalPhotoTypesSection = ({ visit, visitId, trialId, isTrialLocked }) => {
  const [open, setOpen] = useState(false);
  const [localSelectedIds, setLocalSelectedIds] = useState([]);
  const [isAll, setIsAll] = useState(false);
  const [newTypeModalOpen, setNewTypeModalOpen] = useState(false);
  const [newTypeTitle, setNewTypeTitle] = useState("");

  const { data: catalog = [] } = useGetExternalMediaTypes({
    type: "photos",
    trialId,
    enabled: !!trialId,
  });
  const { data: visitConfig } = useGetVisitExternalPhotoTypes({
    visitId,
    select: selectVisitConfig,
  });
  const configured = visitConfig?.external_photo_types ?? [];
  const updateMutation = useUpdateVisitExternalPhotoTypesMutation();
  const createTypeMutation = useCreateExternalMediaTypeMutation();

  const handleCreateType = async () => {
    setNewTypeModalOpen(false);
    if (!newTypeTitle || newTypeTitle.trim() === "") return;
    createTypeMutation.mutate(
      { title: newTypeTitle, uploadType: UPLOAD_TYPES.EXTERNAL_PHOTOS, trialId },
      { onSuccess: () => setNewTypeTitle("") }
    );
  };

  useEffect(() => {
    if (open) {
      setLocalSelectedIds(configured.map((type) => type.id));
      setIsAll(!!visitConfig?.is_all_external_photos);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const allSelected = catalog.length > 0 && localSelectedIds.length === catalog.length;

  const toggleType = (id) => {
    if (isTrialLocked) return;
    setLocalSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((existingId) => existingId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (isTrialLocked) return;
    setLocalSelectedIds(allSelected ? [] : catalog.map((type) => type.id));
  };

  const handleSave = () => {
    updateMutation.mutate(
      {
        visitId,
        ids: localSelectedIds,
        isAllExternalPhotos: canSaveForFollowingVisits(visit) ? isAll : undefined,
      },
      { onSuccess: () => setOpen(false) }
    );
  };

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="subtitle1" fontWeight={600}>
        External Photo Types ({configured.length} configured)
      </Typography>
      <CustomButton size="small" variant="outlined" onClick={() => setOpen(true)}>
        {isTrialLocked ? "View" : "Manage"}
      </CustomButton>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isTrialLocked ? "View" : "Manage"} External Photo Types</DialogTitle>
        <DialogContent dividers>
          {!isTrialLocked && canSaveForFollowingVisits(visit) && (
            <Box sx={{ mb: 1 }}>
              <SaveForFollowingVisitsCheckbox
                label="Save external photo types for all coming visits"
                checked={isAll}
                onChange={(e) => setIsAll(e.target.checked)}
              />
            </Box>
          )}

          {!isTrialLocked && catalog.length > 0 && (
            <Typography
              variant="body2"
              sx={{ cursor: "pointer", color: "primary.main", mb: 1 }}
              onClick={handleSelectAll}
            >
              {allSelected ? "Deselect All: " : "Select All: "}
              {localSelectedIds.length}
            </Typography>
          )}

          <List>
            {catalog.map((type) => (
              <ListItem key={type.id} disableGutters sx={{ mb: 1 }}>
                <Paper sx={{ p: 1.5, display: "flex", alignItems: "center", width: "100%" }}>
                  <Checkbox
                    checked={localSelectedIds.includes(type.id)}
                    disabled={isTrialLocked}
                    onChange={() => toggleType(type.id)}
                  />
                  <ListItemText primary={type.title} />
                </Paper>
              </ListItem>
            ))}
          </List>

          {!isTrialLocked && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ mt: 1 }}
              onClick={() => setNewTypeModalOpen(true)}
            >
              Add New Type
            </Button>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {FALLBACK_HELPER_TEXT}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          {!isTrialLocked && (
            <Button variant="contained" onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {newTypeModalOpen && (
        <AddNewModal
          show={newTypeModalOpen}
          onHide={() => setNewTypeModalOpen(false)}
          formSubmissionApi={handleCreateType}
          modalShow={setNewTypeModalOpen}
          title={newTypeTitle}
          setTitle={setNewTypeTitle}
        />
      )}
    </Stack>
  );
};

const ExternalVideoTypesSection = ({ visit, visitId, trialId, isTrialLocked }) => {
  const [open, setOpen] = useState(false);
  const [localSelectedIds, setLocalSelectedIds] = useState([]);
  const [isAll, setIsAll] = useState(false);
  const [newTypeModalOpen, setNewTypeModalOpen] = useState(false);
  const [newTypeTitle, setNewTypeTitle] = useState("");

  const { data: catalog = [] } = useGetExternalMediaTypes({
    type: "videos",
    trialId,
    enabled: !!trialId,
  });
  const { data: visitConfig } = useGetVisitExternalVideoTypes({
    visitId,
    select: selectVisitConfig,
  });
  const configured = visitConfig?.external_video_types ?? [];
  const updateMutation = useUpdateVisitExternalVideoTypesMutation();
  const createTypeMutation = useCreateExternalMediaTypeMutation();

  const handleCreateType = async () => {
    setNewTypeModalOpen(false);
    if (!newTypeTitle || newTypeTitle.trim() === "") return;
    createTypeMutation.mutate(
      { title: newTypeTitle, uploadType: UPLOAD_TYPES.EXTERNAL_VIDEOS, trialId },
      { onSuccess: () => setNewTypeTitle("") }
    );
  };

  useEffect(() => {
    if (open) {
      setLocalSelectedIds(configured.map((type) => type.id));
      setIsAll(!!visitConfig?.is_all_external_videos);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const allSelected = catalog.length > 0 && localSelectedIds.length === catalog.length;

  const toggleType = (id) => {
    if (isTrialLocked) return;
    setLocalSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((existingId) => existingId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (isTrialLocked) return;
    setLocalSelectedIds(allSelected ? [] : catalog.map((type) => type.id));
  };

  const handleSave = () => {
    updateMutation.mutate(
      {
        visitId,
        ids: localSelectedIds,
        isAllExternalVideos: canSaveForFollowingVisits(visit) ? isAll : undefined,
      },
      { onSuccess: () => setOpen(false) }
    );
  };

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="subtitle1" fontWeight={600}>
        External Video Types ({configured.length} configured)
      </Typography>
      <CustomButton size="small" variant="outlined" onClick={() => setOpen(true)}>
        {isTrialLocked ? "View" : "Manage"}
      </CustomButton>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isTrialLocked ? "View" : "Manage"} External Video Types</DialogTitle>
        <DialogContent dividers>
          {!isTrialLocked && canSaveForFollowingVisits(visit) && (
            <Box sx={{ mb: 1 }}>
              <SaveForFollowingVisitsCheckbox
                label="Save external video types for all coming visits"
                checked={isAll}
                onChange={(e) => setIsAll(e.target.checked)}
              />
            </Box>
          )}

          {!isTrialLocked && catalog.length > 0 && (
            <Typography
              variant="body2"
              sx={{ cursor: "pointer", color: "primary.main", mb: 1 }}
              onClick={handleSelectAll}
            >
              {allSelected ? "Deselect All: " : "Select All: "}
              {localSelectedIds.length}
            </Typography>
          )}

          <List>
            {catalog.map((type) => (
              <ListItem key={type.id} disableGutters sx={{ mb: 1 }}>
                <Paper sx={{ p: 1.5, display: "flex", alignItems: "center", width: "100%" }}>
                  <Checkbox
                    checked={localSelectedIds.includes(type.id)}
                    disabled={isTrialLocked}
                    onChange={() => toggleType(type.id)}
                  />
                  <ListItemText primary={type.title} />
                </Paper>
              </ListItem>
            ))}
          </List>

          {!isTrialLocked && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ mt: 1 }}
              onClick={() => setNewTypeModalOpen(true)}
            >
              Add New Type
            </Button>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {FALLBACK_HELPER_TEXT}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          {!isTrialLocked && (
            <Button variant="contained" onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {newTypeModalOpen && (
        <AddNewModal
          show={newTypeModalOpen}
          onHide={() => setNewTypeModalOpen(false)}
          formSubmissionApi={handleCreateType}
          modalShow={setNewTypeModalOpen}
          title={newTypeTitle}
          setTitle={setNewTypeTitle}
        />
      )}
    </Stack>
  );
};

const ExternalMediaVisitConfig = ({ visit, visitId, trial, isTrialLocked = false }) => {
  return (
    <Box sx={{ width: "100%" }}>
      <Stack gap={1}>
        <ExternalPhotoTypesSection
          visit={visit}
          visitId={visitId}
          trialId={trial?.id}
          isTrialLocked={isTrialLocked}
        />
        <ExternalVideoTypesSection
          visit={visit}
          visitId={visitId}
          trialId={trial?.id}
          isTrialLocked={isTrialLocked}
        />
      </Stack>
    </Box>
  );
};

export default ExternalMediaVisitConfig;
